import type { ClientMessage, ServerMessage } from '../../shared/protocol';

export type SignalingStatus = 'connecting' | 'open' | 'reconnecting' | 'closed';

interface Handlers {
  onMessage(msg: ServerMessage): void;
  onStatus(status: SignalingStatus): void;
  /** Fired on every successful (re)connection; the session sends its join message here. */
  onOpen(reconnected: boolean): void;
}

const BACKOFF_MS = [500, 1000, 2000, 4000, 8000];
const PING_EVERY_MS = 10_000;
const DEAD_AFTER_MS = 25_000;

/** WebSocket wrapper with exponential reconnection and liveness detection. */
export class SignalingClient {
  private ws: WebSocket | null = null;
  private attempts = 0;
  private everOpened = false;
  private stopped = false;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private lastMessageAt = 0;

  constructor(private readonly handlers: Handlers) {}

  connect(): void {
    this.stopped = false;
    this.open();
  }

  private url(): string {
    return `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws`;
  }

  private open(): void {
    this.handlers.onStatus(this.everOpened ? 'reconnecting' : 'connecting');
    const ws = new WebSocket(this.url());
    this.ws = ws;

    ws.onopen = () => {
      const reconnected = this.everOpened;
      this.everOpened = true;
      this.attempts = 0;
      this.lastMessageAt = Date.now();
      this.handlers.onStatus('open');
      this.startPing();
      this.handlers.onOpen(reconnected);
    };
    ws.onmessage = (event) => {
      this.lastMessageAt = Date.now();
      try {
        this.handlers.onMessage(JSON.parse(event.data as string) as ServerMessage);
      } catch (err) {
        console.error('[signaling] mensaje no válido', err);
      }
    };
    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.stopPing();
      this.ws = null;
      if (this.stopped) {
        this.handlers.onStatus('closed');
        return;
      }
      this.scheduleReconnect();
    };
    ws.onerror = () => {
      /* onclose follows and handles the retry */
    };
  }

  private scheduleReconnect(): void {
    const delay = BACKOFF_MS[Math.min(this.attempts, BACKOFF_MS.length - 1)];
    this.attempts++;
    this.handlers.onStatus('reconnecting');
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (!this.stopped) this.open();
    }, delay);
  }

  /** Reconnect right away (e.g. when the browser reports it is back online). */
  retryNow(): void {
    if (this.stopped || this.ws) return;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.open();
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = setInterval(() => {
      if (Date.now() - this.lastMessageAt > DEAD_AFTER_MS) {
        this.ws?.close();
        return;
      }
      this.send({ type: 'ping' });
    }, PING_EVERY_MS);
  }

  private stopPing(): void {
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.pingTimer = null;
  }

  get isOpen(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  send(msg: ClientMessage): boolean {
    if (this.ws?.readyState !== WebSocket.OPEN) return false;
    this.ws.send(JSON.stringify(msg));
    return true;
  }

  close(): void {
    this.stopped = true;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.stopPing();
    const ws = this.ws;
    this.ws = null;
    if (ws && ws.readyState <= WebSocket.OPEN) ws.close(1000, 'bye');
    this.handlers.onStatus('closed');
  }
}
